'use client';

import {RecaptchaScript} from '@gfiber/third-parties/google';
import {US_STATES} from '@gfiber/typesmith/us_states';
import {zodResolver} from '@hookform/resolvers/zod';
import {Typography} from '@pixel-party/Typography/v2';
import {useParams} from 'next/navigation';
import type React from 'react';
import {useId} from 'react';
import {Controller, type SubmitHandler, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useSubmit} from '@/app/forms/_hooks/useSubmit';
import {REGISTRY} from '@/app/forms/_registry/client';
import * as constants from '@/app/forms/constants';
import {
  ACCOUNT_ACTION,
  ACTION_OPTIONS_MAP,
  LEGAL_STATUS,
  STATUS_OPTIONS_MAP,
  YES_NO_MAP,
} from '@/app/forms/i18n';
import {ApiError, type InferFormValues} from '@/app/forms/types';
import {
  getRootError,
  getSchemaShape,
  getSfdcLanguage,
  getTodayDateString,
  handleError,
  onSubmitError,
  tSafe,
} from '@/app/forms/utils';
import {FormError, Radio} from '@/components';
import {FileUpload} from '@/components/FileUpload/FileUpload';
import {SelectField, SelectItem} from '@/components/SelectField/SelectField';
import {TextField} from '@/components/TextField/TextField';
import {isRequired, maxLength} from '@/lib/utils/common';
import {Consent} from '../Consent/Consent';
import {Description, FormDescription} from '../Description/Description';
import {SubmitButton} from '../SubmitButton/SubmitButton';
import {SuccessMessage} from '../SuccessMessage/SuccessMessage';
import styles from './FiberDeceasedForm.module.scss';

function FiberDeceasedForm(): React.JSX.Element {
  const {t} = useTranslation();

  const config = REGISTRY.FiberDeceasedForm;
  const params = useParams();
  type FiberDeceasedFormData = InferFormValues<typeof config>;

  const {
    control,
    formState: {errors, isSubmitted, isValid},
    handleSubmit,
    setError,
    watch,
  } = useForm<FiberDeceasedFormData>({
    resolver: zodResolver(config.schema),
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      phoneNumber: '',
      email: '',
      legalStatus: '',
      dateOfDeath: '',
      depCustomerStatus: '',
      deathCert: [],
      depIncapDate: '',
      legalAuthorizationDoc: [],
      identityVerificationDoc: [],
      accountAction: '',
      transferRecipientEmail: '',
      fullName: '',
      gfiberAccount: '',
      homeAddress: '',
      state: '',
      attachments: [],
      formType: 'Deceased_Transfer',
      language: getSfdcLanguage(params?.locale),
    },
  });
  const fields = getSchemaShape(config.schema);
  const {mutate, isPending, isSuccess} = useSubmit<FiberDeceasedFormData>(
    config.id,
  );
  const id = useId();

  const onSubmit: SubmitHandler<FiberDeceasedFormData> = async data => {
    const recaptchaToken = await window.grecaptcha?.enterprise.execute(
      constants.RECAPTCHA_SITE_KEY,
      {action: config.recaptchaAction || 'submit_form'},
    );

    if (!recaptchaToken) {
      handleError(
        new ApiError(t('errors.recaptcha'), {
          code: 'RECAPTCHA_FAILED',
          message: 'reCAPTCHA token could not be obtained',
        }),
        setError,
        fields,
      );
      return;
    }

    mutate(
      {data, recaptchaToken},
      {
        onError: error => {
          handleError(error, setError, fields);
        },
      },
    );
  };

  return (
    <>
      <h1 className="title">{t(config.title)}</h1>
      {isSuccess ? (
        <SuccessMessage />
      ) : (
        <form
          className="form"
          onSubmit={handleSubmit(onSubmit, onSubmitError)}
          noValidate
        >
          <fieldset disabled={isPending}>
            <FormDescription>
              <Description>{t('forms.fiberDeceased.description1')}</Description>
              <Description>{t('forms.fiberDeceased.description2')}</Description>
            </FormDescription>
            <div className="field">
              <Controller
                control={control}
                name="firstName"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.fiberDeceased.firstName')}
                    id={`firstName-${id}`}
                    required={isRequired(fields.firstName)}
                    errorText={tSafe(errors.firstName?.message, t)}
                    maxLength={maxLength(fields.firstName)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="lastName"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.fiberDeceased.lastName')}
                    id={`lastName-${id}`}
                    required={isRequired(fields.lastName)}
                    errorText={tSafe(errors.lastName?.message, t)}
                    maxLength={maxLength(fields.lastName)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="phoneNumber"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.fiberDeceased.phoneNumber')}
                    id={`phoneNumber-${id}`}
                    required={isRequired(fields.phoneNumber)}
                    errorText={tSafe(errors.phoneNumber?.message, t)}
                    maxLength={maxLength(fields.phoneNumber)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="email"
                render={({field}) => (
                  <TextField
                    {...field}
                    type="email"
                    label={t('forms.fiberDeceased.email')}
                    id={`email-${id}`}
                    required={isRequired(fields.email)}
                    errorText={tSafe(errors.email?.message, t)}
                    maxLength={maxLength(fields.email)}
                  />
                )}
              />
            </div>
            <fieldset className="field">
              <legend className="legend">
                {t('forms.fiberDeceased.legalStatusLabel')}{' '}
                {isRequired(fields.legalStatus) && '*'}
              </legend>
              <Controller
                name="legalStatus"
                control={control}
                render={({field: {onChange, value, ref}}) => (
                  <div
                    className="radioGroup"
                    ref={ref}
                  >
                    {STATUS_OPTIONS_MAP.map(item => (
                      <Radio
                        key={item.id}
                        value={item.value}
                        label={t(item.i18nKey, {defaultValue: item.id})}
                        id={`legalStatus-${id}-${item.id}`}
                        name="legalStatus"
                        checked={value === item.value}
                        onChange={onChange}
                      />
                    ))}
                  </div>
                )}
              />
              <FormError message={tSafe(errors.legalStatus?.message, t)} />
            </fieldset>

            {watch('legalStatus') === LEGAL_STATUS.DECEASED && (
              <div>
                <div className="textHighlight">
                  <div className="field">
                    <Controller
                      control={control}
                      name="dateOfDeath"
                      render={({field}) => (
                        <TextField
                          {...field}
                          type="date"
                          max={getTodayDateString()}
                          label={t('forms.fiberDeceased.dateOfDeath')}
                          id={`dateOfDeath-${id}`}
                          required={isRequired(fields.dateOfDeath)}
                          errorText={tSafe(errors.dateOfDeath?.message, t)}
                          maxLength={maxLength(fields.dateOfDeath)}
                        />
                      )}
                    />
                  </div>
                </div>
                <div className="radioHighlight">
                  <fieldset className="field">
                    <legend className="legend">
                      {t('forms.fiberDeceased.depCustomerStatusLabel')} *
                    </legend>
                    <Controller
                      name="depCustomerStatus"
                      control={control}
                      render={({field: {onChange, value, ref}}) => (
                        <div
                          className="radioGroup"
                          ref={ref}
                        >
                          {YES_NO_MAP.map(item => (
                            <Radio
                              key={item.id}
                              id={`depCustomerStatus-${id}-${item.id}`}
                              name="depCustomerStatus"
                              value={item.value}
                              label={t(item.i18nKey, {
                                defaultValue: item.id,
                              })}
                              checked={value === item.value}
                              onChange={onChange}
                            />
                          ))}
                        </div>
                      )}
                    />
                    <FormError
                      message={tSafe(errors.depCustomerStatus?.message, t)}
                    />
                  </fieldset>
                </div>
                <div className="field radioHighlight">
                  <Controller
                    name="deathCert"
                    control={control}
                    render={({field}) => (
                      <>
                        <FileUpload
                          value={field.value as File[]}
                          instruction={t(
                            'forms.fiberDeceased.deathCertInstruction',
                          )}
                          buttonLabel={t('fileUpload.buttonLabel')}
                          defaultFileName={t('fileUpload.defaultFileName')}
                          fileAttachedLabel={t('fileUpload.fileAttachedLabel')}
                          maxFileSizeErrorMessage={t('errors.fileTooBig', {
                            size: constants.MAX_FILE_SIZE_MB,
                          })}
                          fileTypeErrorMessage={t('errors.invalidFileType')}
                          accept={constants.ACCEPTED_FILE_TYPES_STRING}
                          maxFiles={5}
                          onFileSelect={(_id, files) => field.onChange(files)}
                          required={true}
                        />
                        <FormError
                          message={tSafe(errors.deathCert?.message, t, {
                            size: constants.MAX_FILE_SIZE_MB,
                            types: constants.ACCEPTED_FILE_TYPES_STRING,
                          })}
                        />
                      </>
                    )}
                  />
                </div>
              </div>
            )}
            {watch('legalStatus') === LEGAL_STATUS.INCAPACITATED && (
              <div>
                <div className="textHighlight">
                  <div className="field">
                    <Controller
                      control={control}
                      name="depIncapDate"
                      render={({field}) => (
                        <TextField
                          {...field}
                          type="date"
                          max={getTodayDateString()}
                          label={t('forms.fiberDeceased.depIncapDate')}
                          id={`depIncapDate-${id}`}
                          required={isRequired(fields.depIncapDate)}
                          errorText={tSafe(errors.depIncapDate?.message, t)}
                          maxLength={maxLength(fields.depIncapDate)}
                        />
                      )}
                    />
                  </div>
                </div>
                <div className="field radioHighlight">
                  <Controller
                    name="legalAuthorizationDoc"
                    control={control}
                    render={({field}) => (
                      <>
                        <FileUpload
                          value={field.value as File[]}
                          instruction={t(
                            'forms.fiberDeceased.legalAuthInstruction',
                          )}
                          buttonLabel={t('fileUpload.buttonLabel')}
                          defaultFileName={t('fileUpload.defaultFileName')}
                          fileAttachedLabel={t('fileUpload.fileAttachedLabel')}
                          maxFileSizeErrorMessage={t('errors.fileTooBig', {
                            size: constants.MAX_FILE_SIZE_MB,
                          })}
                          fileTypeErrorMessage={t('errors.invalidFileType')}
                          accept={constants.ACCEPTED_FILE_TYPES_STRING}
                          maxFiles={5}
                          onFileSelect={(_id, files) => field.onChange(files)}
                          required={true}
                        />
                        <FormError
                          message={tSafe(
                            errors.legalAuthorizationDoc?.message,
                            t,
                            {
                              size: constants.MAX_FILE_SIZE_MB,
                              types: constants.ACCEPTED_FILE_TYPES_STRING,
                            },
                          )}
                        />
                      </>
                    )}
                  />
                </div>
                <div className="field radioHighlight">
                  <Controller
                    name="identityVerificationDoc"
                    control={control}
                    render={({field}) => (
                      <>
                        <FileUpload
                          value={field.value as File[]}
                          instruction={t(
                            'forms.fiberDeceased.idVerificationInstruction',
                          )}
                          buttonLabel={t('fileUpload.buttonLabel')}
                          defaultFileName={t('fileUpload.defaultFileName')}
                          fileAttachedLabel={t('fileUpload.fileAttachedLabel')}
                          maxFileSizeErrorMessage={t('errors.fileTooBig', {
                            size: constants.MAX_FILE_SIZE_MB,
                          })}
                          fileTypeErrorMessage={t('errors.invalidFileType')}
                          accept={constants.ACCEPTED_FILE_TYPES_STRING}
                          maxFiles={5}
                          onFileSelect={(_id, files) => field.onChange(files)}
                          required={true}
                        />
                        <FormError
                          message={tSafe(
                            errors.identityVerificationDoc?.message,
                            t,
                            {
                              size: constants.MAX_FILE_SIZE_MB,
                              types: constants.ACCEPTED_FILE_TYPES_STRING,
                            },
                          )}
                        />
                      </>
                    )}
                  />
                </div>
              </div>
            )}
            <fieldset className="field">
              <legend className="legend">
                {t('forms.fiberDeceased.accountActionLabel')}{' '}
                {isRequired(fields.accountAction) && '*'}
              </legend>
              <Controller
                name="accountAction"
                control={control}
                render={({field: {onChange, value, ref}}) => (
                  <div
                    className="radioGroup"
                    ref={ref}
                  >
                    {ACTION_OPTIONS_MAP.map(item => (
                      <Radio
                        key={item.id}
                        value={item.value}
                        label={t(item.i18nKey, {defaultValue: item.id})}
                        id={`accountAction-${id}-${item.id}`}
                        name="accountAction"
                        checked={value === item.value}
                        onChange={onChange}
                      />
                    ))}
                  </div>
                )}
              />
              <FormError message={tSafe(errors.accountAction?.message, t)} />
            </fieldset>
            {watch('accountAction') === ACCOUNT_ACTION.TRANSFER && (
              <div className="textHighlight">
                <div className="field">
                  <Controller
                    control={control}
                    name="transferRecipientEmail"
                    render={({field}) => (
                      <TextField
                        {...field}
                        type="email"
                        label={t('forms.fiberDeceased.transferRecipientEmail')}
                        id={`transferRecipientEmail-${id}`}
                        required={isRequired(fields.transferRecipientEmail)}
                        errorText={tSafe(
                          errors.transferRecipientEmail?.message,
                          t,
                        )}
                        maxLength={maxLength(fields.transferRecipientEmail)}
                        errorTextClass={styles.transferEmail}
                      />
                    )}
                  />
                  <Typography
                    use="caption"
                    className="helpText"
                  >
                    {t('forms.fiberDeceased.transferEmailWarning')}
                  </Typography>
                </div>
              </div>
            )}
            <div className="field">
              <Typography
                use="subtitle1"
                className="description subtitle"
              >
                {t('forms.fiberDeceased.aboutCustomer')}
              </Typography>
            </div>
            <div className="field">
              <Controller
                control={control}
                name="fullName"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.fiberDeceased.fullName')}
                    id={`fullName-${id}`}
                    required={isRequired(fields.fullName)}
                    errorText={tSafe(errors.fullName?.message, t)}
                    maxLength={maxLength(fields.fullName)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="gfiberAccount"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.fiberDeceased.gfiberAccount')}
                    id={`gfiberAccount-${id}`}
                    required={isRequired(fields.gfiberAccount)}
                    errorText={tSafe(errors.gfiberAccount?.message, t)}
                    maxLength={maxLength(fields.gfiberAccount)}
                  />
                )}
              />
            </div>

            <div className="field">
              <Controller
                control={control}
                name="homeAddress"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.fiberDeceased.homeAddress')}
                    id={`homeAddress-${id}`}
                    required={isRequired(fields.homeAddress)}
                    errorText={tSafe(errors.homeAddress?.message, t)}
                    maxLength={maxLength(fields.homeAddress)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                name="state"
                control={control}
                render={({field, fieldState: {error}}) => (
                  <SelectField
                    {...field}
                    placeholder={t('forms.shared.selectOne')}
                    invalid={!!error}
                    label={t('forms.fiberDeceased.state')}
                    required={isRequired(fields.state)}
                    errorText={tSafe(errors.state?.message, t)}
                  >
                    {US_STATES.map(state => (
                      <SelectItem
                        key={state.code}
                        value={state.code}
                      >
                        {state.name}
                      </SelectItem>
                    ))}
                  </SelectField>
                )}
              />
            </div>

            <div className="field">
              <Controller
                name="attachments"
                control={control}
                render={({field}) => (
                  <>
                    <FileUpload
                      value={field.value as File[]}
                      instruction={t(
                        'forms.fiberDeceased.attachmentsInstruction',
                      )}
                      buttonLabel={t('fileUpload.buttonLabel')}
                      defaultFileName={t('fileUpload.defaultFileName')}
                      fileAttachedLabel={t('fileUpload.fileAttachedLabel')}
                      maxFileSizeErrorMessage={t('errors.fileTooBig', {
                        size: constants.MAX_FILE_SIZE_MB,
                      })}
                      fileTypeErrorMessage={t('errors.invalidFileType')}
                      accept={constants.ACCEPTED_FILE_TYPES_STRING}
                      maxFiles={5}
                      onFileSelect={(_id, files) => field.onChange(files)}
                    />
                    <FormError
                      message={tSafe(errors.attachments?.message, t, {
                        size: constants.MAX_FILE_SIZE_MB,
                        types: constants.ACCEPTED_FILE_TYPES_STRING,
                      })}
                    />
                  </>
                )}
              />
            </div>
            <SubmitButton
              isDisabled={isPending}
              isPending={isPending}
              rootError={getRootError(errors, isSubmitted, isValid, t)}
            />
            <Consent />
          </fieldset>
          {constants.RECAPTCHA_SITE_KEY && (
            <RecaptchaScript siteKey={constants.RECAPTCHA_SITE_KEY} />
          )}
        </form>
      )}
    </>
  );
}

export {FiberDeceasedForm};
